<script setup>
import { ref, computed } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LiveChatCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/LiveChatCampaign/LiveChatCampaignForm.vue';

const props = defineProps({
  selectedCampaign: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const liveChatCampaignFormRef = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isUpdatingCampaign = computed(() => uiFlags.value.isUpdating);

const isInvalidForm = computed(
  () => liveChatCampaignFormRef.value?.isSubmitDisabled
);

const selectedCampaignId = computed(() => props.selectedCampaign.id);

// GrowerIQ: show how this campaign has actually performed before anyone edits
// the copy. A campaign that wins emails should be left alone; one that has
// fired plenty and never converted is free to rewrite.
const stats = computed(() => props.selectedCampaign?.stats || {});
const fires = computed(() => stats.value.fires ?? 0);
const replied = computed(() => stats.value.replied ?? 0);
const emails = computed(() => stats.value.emails ?? 0);
const replyRate = computed(() =>
  fires.value ? Math.round((replied.value / fires.value) * 100) : 0
);
const verdict = computed(() => {
  if (!fires.value) return 'Never fired yet - safe to rewrite.';
  if (emails.value > 0)
    return `Converting: ${emails.value} email${emails.value === 1 ? '' : 's'} captured. Change with care.`;
  if (replied.value > 0) return 'Gets replies but no emails yet - the ask could be stronger.';
  return `Fired ${fires.value} time${fires.value === 1 ? '' : 's'} with no reply - free to rewrite.`;
});

const updateCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/update', {
      id: selectedCampaignId.value,
      ...campaignDetails,
    });

    useAlert(t('CAMPAIGN.LIVE_CHAT.EDIT.FORM.API.SUCCESS_MESSAGE'));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.LIVE_CHAT.EDIT.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleSubmit = () => {
  updateCampaign(liveChatCampaignFormRef.value.prepareCampaignDetails());
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('CAMPAIGN.LIVE_CHAT.EDIT.TITLE')"
    :is-loading="isUpdatingCampaign"
    :disable-confirm-button="isUpdatingCampaign || isInvalidForm"
    overflow-y-auto
    @confirm="handleSubmit"
  >
    <div
      class="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 mb-4 rounded-lg bg-n-alpha-2"
    >
      <div class="flex flex-col">
        <span class="text-lg font-medium text-n-slate-12">{{ fires }}</span>
        <span class="text-xs text-n-slate-11">Fires</span>
      </div>
      <div class="flex flex-col">
        <span class="text-lg font-medium text-n-slate-12">
          {{ replied }}
          <span v-if="fires" class="text-xs font-normal text-n-slate-11">({{ replyRate }}%)</span>
        </span>
        <span class="text-xs text-n-slate-11">Replied</span>
      </div>
      <div class="flex flex-col">
        <span
          class="text-lg font-medium"
          :class="emails > 0 ? 'text-n-teal-11' : 'text-n-slate-12'"
        >
          {{ emails }}
        </span>
        <span class="text-xs text-n-slate-11">Emails captured</span>
      </div>
      <span class="flex-1 min-w-[14rem] text-sm text-n-slate-11">{{ verdict }}</span>
    </div>
    <LiveChatCampaignForm
      ref="liveChatCampaignFormRef"
      mode="edit"
      :selected-campaign="selectedCampaign"
      :show-action-buttons="false"
      @submit="handleSubmit"
    />
  </Dialog>
</template>
